import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { ArrowLeft, ShieldCheck, Sparkles, Star } from 'lucide-react';
import LanguageSelector from './LanguageSelector';
import { useLanguage } from '../context/LanguageContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

const TRUST_STATS_BASE = [
  { id: 'machines', valueKey: 'machines', labelKey: 'trustStatMachines' },
  { id: 'rating', value: '4.9', labelKey: 'trustStatRating', icon: Star },
  { id: 'gst', value: 'GST', labelKey: 'trustStatCompliant' },
];

export default function AuthPremiumShell({ title, subtitle, backTo = '/login', backLabelKey = 'backToLogin', children }) {
  const { t } = useLanguage();
  const [machineCountLabel, setMachineCountLabel] = useState('…');

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE_URL}/api/public/machines/count`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data) => {
        if (cancelled) return;
        const n = Number(data?.count);
        setMachineCountLabel(Number.isFinite(n) && n >= 0 ? String(n) : '—');
      })
      .catch(() => {
        if (!cancelled) setMachineCountLabel('—');
      });
    return () => { cancelled = true; };
  }, []);

  const trustStats = TRUST_STATS_BASE.map((stat) => (
    stat.valueKey === 'machines'
      ? { ...stat, value: machineCountLabel }
      : stat
  ));

  return (
    <div className="login-page login-page--ultra relative min-h-dvh overflow-x-hidden bg-[#050d1a] font-sans text-white">
      <div className="pointer-events-none fixed inset-0 bg-[linear-gradient(rgba(201,168,76,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(201,168,76,0.03)_1px,transparent_1px)] bg-[size:48px_48px]" aria-hidden />
      <div className="pointer-events-none fixed -right-32 -top-32 h-[520px] w-[520px] rounded-full bg-[radial-gradient(circle,rgba(201,168,76,0.12)_0%,transparent_70%)]" aria-hidden />
      <div className="pointer-events-none fixed -bottom-40 -left-32 h-[480px] w-[480px] rounded-full bg-[radial-gradient(circle,rgba(15,32,64,0.95)_0%,transparent_70%)]" aria-hidden />
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(ellipse_at_20%_0%,rgba(201,168,76,0.06),transparent_50%),radial-gradient(ellipse_at_80%_100%,rgba(10,22,40,0.8),transparent_55%)]" aria-hidden />

      <div className="login-lang-bar login-lang-bar--desktop fixed z-50 hidden md:block">
        <LanguageSelector compact />
      </div>

      <div className="login-page__wrapper relative z-10 mx-auto flex min-h-dvh w-full max-w-6xl flex-col px-4 pb-8 pt-[calc(env(safe-area-inset-top,0px)+16px)] md:flex-row md:items-center md:gap-10 md:px-6 md:pb-10 md:pt-8 lg:gap-16 lg:px-8 lg:pb-12 lg:pt-10">
        <aside className="login-hero hidden flex-1 flex-col justify-center md:flex animate-fade-in">
          <div className="mb-8 inline-flex w-fit items-center gap-2 rounded-full border border-de-gold/25 bg-de-gold/10 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-de-gold/90">
            <Sparkles className="h-3.5 w-3.5" />
            {t('platform')}
          </div>

          <div className="mb-6 flex items-center gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-de-logo text-lg font-black text-[#0a1628] shadow-[0_0_40px_rgba(201,168,76,0.35)]">
              DE
            </div>
            <div>
              <h1 className="de-text-gradient text-3xl font-extrabold tracking-tight xl:text-4xl">
                {t('appName')}
              </h1>
              <p className="mt-1 text-xs font-semibold uppercase tracking-[0.25em] text-de-gold/60">
                {t('tagline')}
              </p>
            </div>
          </div>

          <p className="max-w-md text-lg font-medium leading-relaxed text-white/80 xl:text-xl">
            {t('landingHeroLine1')}
          </p>
          <p className="mt-3 max-w-sm text-sm leading-relaxed text-de-muted">
            {t('platform')} — {t('marketingBuiltFor')}
          </p>

          <div className="mt-10 grid max-w-md grid-cols-3 gap-3">
            {trustStats.map((stat) => (
              <div
                key={stat.labelKey}
                className="de-glass rounded-2xl border border-white/10 px-4 py-4 text-center"
              >
                <p className="flex items-center justify-center gap-1 text-xl font-bold text-de-gold">
                  {stat.icon && <stat.icon className="h-4 w-4 fill-de-gold text-de-gold" />}
                  {stat.value}
                </p>
                <p className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-de-muted">
                  {t(stat.labelKey)}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-8 flex items-center gap-2 text-xs text-de-muted">
            <ShieldCheck className="h-4 w-4 text-emerald-400" />
            {t('landingTrustLine')}
          </div>
        </aside>

        <main className="mx-auto w-full max-w-[420px] flex-shrink-0 md:mx-0">
          <header className="login-mobile-header login-logo-section login-logo-section--premium mb-6 flex items-start justify-between gap-3 md:hidden">
            <div className="flex min-w-0 flex-1 items-center gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-de-logo text-sm font-black text-[#0a1628] shadow-[0_0_24px_rgba(201,168,76,0.3)]">
                DE
              </div>
              <div className="min-w-0">
                <p className="de-text-gradient truncate text-base font-extrabold">{t('appName')}</p>
                <p className="truncate text-[9px] font-semibold uppercase tracking-[0.15em] text-de-gold/55">{t('tagline')}</p>
              </div>
            </div>
            <LanguageSelector compact iconOnly />
          </header>

          <div className="login-page__card login-page__card--ultra de-glass de-glow-gold overflow-hidden rounded-3xl border border-de-gold/20 bg-gradient-to-br from-[#0f2040]/90 to-[#0a1628]/95 p-6 shadow-[0_32px_80px_rgba(0,0,0,0.55)] sm:p-7">
            <Link
              to={backTo}
              className="mb-4 flex items-center gap-1.5 text-[12px] font-medium leading-snug text-white/55 transition-colors hover:text-[#C9A84C]"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              {t(backLabelKey)}
            </Link>

            <div className="mb-6">
              <h2 className="text-2xl font-extrabold tracking-tight text-white sm:text-[1.65rem]">
                {title}
              </h2>
              {subtitle && (
                <p className="mt-1.5 text-sm leading-relaxed text-de-muted">{subtitle}</p>
              )}
            </div>

            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
